import { useState } from 'react';

const EMPTY_VARIANT = { variantKey: '', label: '', weight: 1 };

export const DEFAULT_WINNER_RULE =
  'Advisory lifetime comparison after 3841 exposures per variant. Not a ship button.';

export function draftFromExperiment(experiment) {
  const guardrail = experiment?.guardrails?.[0] || {};
  const variants = (experiment?.variants || []).slice(0, 2).map((variant) => ({
    variantKey: variant.variantKey || '',
    label: variant.label || '',
    weight: Number(variant.weight) || 1,
  }));
  while (variants.length < 2) variants.push({ ...EMPTY_VARIANT });
  return {
    name: experiment?.name || '',
    description: experiment?.description || '',
    surface: experiment?.surface || 'marketing',
    pagePath: experiment?.pagePath || '/',
    primaryGoal: experiment?.primaryGoal || '',
    primaryGoalLabel: experiment?.primaryGoalLabel || '',
    guardrailGoal: guardrail.goal || '',
    guardrailLabel: guardrail.label || '',
    winnerRule: experiment?.winnerRule || DEFAULT_WINNER_RULE,
    variants,
  };
}

function Field({ label, children }) {
  return (
    <label className="experiment-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function ExperimentDraftForm({
  mode = 'edit',
  initialKey = '',
  initialDraft,
  structureLocked = false,
  busy = false,
  error = '',
  onSubmit,
}) {
  const [key, setKey] = useState(initialKey);
  const [draft, setDraft] = useState(initialDraft || draftFromExperiment(null));
  const [previewKey, setPreviewKey] = useState(draft.variants[0]?.variantKey || 'control');
  const preview = draft.variants.find((variant) => variant.variantKey === previewKey) || draft.variants[0];

  const setField = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const setVariant = (index, field) => (event) => {
    const value = field === 'weight' ? Number(event.target.value) : event.target.value;
    setDraft((current) => ({
      ...current,
      variants: current.variants.map((variant, variantIndex) => (
        variantIndex === index ? { ...variant, [field]: value } : variant
      )),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      key: key.trim(),
      draft: {
        ...draft,
        name: draft.name.trim(),
        description: draft.description.trim(),
        pagePath: draft.pagePath.trim(),
        primaryGoal: draft.primaryGoal.trim(),
        primaryGoalLabel: draft.primaryGoalLabel.trim(),
        guardrailGoal: draft.guardrailGoal.trim(),
        guardrailLabel: draft.guardrailLabel.trim(),
        winnerRule: draft.winnerRule.trim(),
        variants: draft.variants.map((variant) => ({
          variantKey: variant.variantKey.trim(),
          label: variant.label.trim(),
          weight: Number(variant.weight) || 1,
        })),
      },
    });
  };

  return (
    <form className="experiment-draft-form" onSubmit={handleSubmit}>
      <h2>{mode === 'create' ? 'New draft' : 'Edit draft'}</h2>
      <p className="experiment-meta">
        Planned sample stays 3,841 per arm. Two variants. Saving does not start traffic.
      </p>
      {mode === 'create' ? (
        <Field label="Key">
          <input
            value={key}
            onChange={(event) => setKey(event.target.value)}
            required
            pattern="[a-z][a-z0-9_]{0,31}"
            autoComplete="off"
          />
        </Field>
      ) : null}
      <Field label="Name">
        <input value={draft.name} onChange={setField('name')} required maxLength={120} />
      </Field>
      <Field label="Description">
        <textarea value={draft.description} onChange={setField('description')} required maxLength={2000} rows={3} />
      </Field>
      <div className="experiment-form-grid">
        <Field label="Surface">
          <select value={draft.surface} onChange={setField('surface')}>
            <option value="marketing">Marketing</option>
            <option value="app">App</option>
          </select>
        </Field>
        <Field label="Page path">
          <input
            value={draft.pagePath}
            onChange={setField('pagePath')}
            required
            disabled={structureLocked}
          />
        </Field>
        <Field label="Primary goal key">
          <input
            value={draft.primaryGoal}
            onChange={setField('primaryGoal')}
            required
            disabled={structureLocked}
          />
        </Field>
        <Field label="Primary goal label">
          <input value={draft.primaryGoalLabel} onChange={setField('primaryGoalLabel')} required />
        </Field>
        <Field label="Guardrail key">
          <input
            value={draft.guardrailGoal}
            onChange={setField('guardrailGoal')}
            required
            disabled={structureLocked}
          />
        </Field>
        <Field label="Guardrail label">
          <input value={draft.guardrailLabel} onChange={setField('guardrailLabel')} required />
        </Field>
      </div>
      {draft.variants.map((variant, index) => (
        <fieldset key={index} className="experiment-variant-fields">
          <legend>Variant {index + 1}</legend>
          <Field label="Key">
            <input
              value={variant.variantKey}
              onChange={setVariant(index, 'variantKey')}
              required
              disabled={structureLocked}
            />
          </Field>
          <Field label="Label">
            <input value={variant.label} onChange={setVariant(index, 'label')} required />
          </Field>
          <Field label="Weight">
            <input
              type="number"
              min="1"
              max="100"
              value={variant.weight}
              onChange={setVariant(index, 'weight')}
              required
            />
          </Field>
        </fieldset>
      ))}
      <Field label="Winner rule">
        <textarea value={draft.winnerRule} onChange={setField('winnerRule')} required maxLength={500} rows={2} />
      </Field>
      {structureLocked ? (
        <p className="experiment-meta">Page, goal keys, and variant keys stay fixed on a mounted experiment.</p>
      ) : null}
      <section className="experiment-preview" aria-label="Draft copy preview">
        <h3>Copy preview</h3>
        <div className="experiment-preview-switch" role="group" aria-label="Preview draft variant">
          {draft.variants.map((variant, index) => (
            <button
              key={variant.variantKey || index}
              type="button"
              aria-pressed={(variant.variantKey || index) === (preview?.variantKey || 0)}
              onClick={() => setPreviewKey(variant.variantKey)}
            >
              {variant.label || variant.variantKey || `Variant ${index + 1}`}
            </button>
          ))}
        </div>
        <p className="experiment-preview-specimen" data-variant={preview?.variantKey || 'control'}>
          {preview?.label || 'Variant label'}
        </p>
      </section>
      {error ? <p className="module-status module-status-error" role="alert">{error}</p> : null}
      <button type="submit" className="experiment-save" disabled={busy}>
        {busy ? 'Saving…' : mode === 'create' ? 'Create draft' : 'Save draft'}
      </button>
    </form>
  );
}
